import { useDialog } from "lib/@hooks/useDialog"
import { PreviewDialog } from "lib/@components/document-viewer/preview-dialog"
import { OneCard } from "lib/@components/one-card"
import { Box, ImageList, ImageListItem, ImageListItemBar, Tab, Typography } from "@mui/material"
import { TabPanel } from "@mui/lab"
import { DocumentList } from "lib/@components/document-viewer/document-list"
import { ListItemBox } from "lib/@components/ListItemBox"
import { ContextMenuBar } from "lib/@components/context-menu-bar"
import { StatsPanels, StatsTabs } from "slot-definitions"
import { Settings } from "lib/@components/settings"
import { replaceBrokenImage } from "lib/replace-broken-image"
import { useHeaderMediaCollapse } from "routes/schedule/my-schedules/my-group-types/use-header-media-collapse"
import { useTheme } from "@mui/material/styles"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useSearchParam } from "lib/@hooks/use-search-param"

export function SpaceCompleteCard({ group, space = group, hideMenu }) {
    const preview = useDialog(<PreviewDialog />, { maxWidth: "lg", fullWidth: true })
    const { isPDF, isShare } = useBoundContext()
    const [shareParam] = useSearchParam("share", null)
    const isShareView = !!isShare || !!shareParam
    const { collapsed, headerRef } = useHeaderMediaCollapse({ disabled: isPDF || !isShareView })
    const theme = useTheme()
    const mediaHeight = collapsed ? 6 : 260
    const mediaBackground = collapsed ? "primary.main" : "transparent"
    const mediaTransition = theme.transitions.create(["height", "background-color"], {
        duration: theme.transitions.duration.shorter,
    })

    StatsTabs.usePlug(space?.description && <Tab label="Info" value="Description" />)
    StatsTabs.usePlug(!!space?.documents?.documents?.length && <Tab label="Documents" value="Documents" />)
    StatsTabs.usePlug(!!space?.pictures?.documents?.length && <Tab label="Pictures" value="Pictures" />)

    StatsPanels.usePlug(
        <TabPanel value="Description" sx={{ maxHeight: "25vh", overflow: "auto" }}>
            <Box dangerouslySetInnerHTML={{ __html: space.description }} />
        </TabPanel>
    )
    StatsPanels.usePlug(
        <TabPanel value="Documents">
            <DocumentList value={space.documents} />
        </TabPanel>
    )
    StatsPanels.usePlug(
        <TabPanel value="Pictures">
            {!!space.pictures?.documents?.length && (
                <ImageList variant="masonry" cols={2} gap={2}>
                    {space.pictures?.documents?.map((picture, index) => (
                        <ImageListItem onClick={() => preview({ name: picture.name, url: picture.href })} key={index}>
                            <img src={picture.href} alt={picture.name} />
                            <ImageListItemBar title={picture.name} />
                        </ImageListItem>
                    ))}
                </ImageList>
            )}
        </TabPanel>
    )

    return (
        !!space && (
            <OneCard sx={{ textAlign: "center", mb: 1, position: "relative" }}>
                {!!space.image && (
                    <Box
                        ref={headerRef}
                        data-cy="collapsible-header-media"
                        sx={{
                            position: "relative",
                            height: mediaHeight,
                            overflow: "hidden",
                            bgcolor: mediaBackground,
                            transition: mediaTransition,
                        }}
                    >
                        <Box
                            width={1}
                            component="img"
                            onError={replaceBrokenImage}
                            sx={{ objectFit: "cover" }}
                            src={space.image}
                            height="100%"
                            color="#333"
                            overflow="hidden"
                        />
                    </Box>
                )}

                <Typography variant="subtitle1" sx={{ mt: 1 }}>
                    {space.name ?? "Not named"}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {space.location}
                </Typography>

                {!hideMenu && (
                    <Settings>
                        <ListItemBox sx={{ position: "absolute", top: 5, right: 5, width: "100%" }}>
                            <Box flex={1} />
                            <Box>
                                <ContextMenuBar type="spaceCard" />
                            </Box>
                        </ListItemBox>
                    </Settings>
                )}
            </OneCard>
        )
    )
}
